<script>
    import NavigationHeader from '../NavigationHeader.vue';
    import AccountService from '@/services/AccountService';
    import ButtonNormal from '@/components/buttons/ButtonNormal.vue';

    import AccountFormError from './AccountFormError';
    import {email, required} from 'vuelidate/lib/validators';

    export default {
        name: 'Account',
        components: {
            NavigationHeader,
            AccountFormError,
            ButtonNormal,
        },
        data() {
            return {
                previewMode: true,
                form: {
                    customer_id: '',
                    company: '',
                    salutation: '',
                    street: '',
                    firstname: '',
                    lastname: '',
                    zip: '',
                    location: '',
                    email: '',
                    country: '',
                    tel: '',
                    bank: '',
                    iban: '',
                    bic: '',
                },
                languageMapping: {
                    de: 'Deutschland',
                    at: 'Österreich',
                    ch: 'Schweiz',
                    fr: 'Frankreich',
                    Deutschland: 'de',
                    Österreich: 'at',
                    Schweiz: 'ch',
                    Frankreich: 'fr',
                },
                error: {},
            };
        },
        validations: {
            form: {
                email: {
                    email,
                    required,
                },
            },
        },
        created() {
            this.init();
        },
        methods: {
            init() {
                AccountService.getAccount().then(response => {
                    if (response.status === 200) {
                        this.form = {
                            ...this.form,
                            ...response.data,
                        };
                        if (this.form.country in this.languageMapping) {
                            this.form.country = this.languageMapping[this.form.country];
                        }
                    }
                });
            },
            submit() {
                this.error = {};
                AccountService.updateAccount(this.getUpdateData()).then(response => {
                    if (response.data.success) {
                        this.init();
                        this.previewMode = true;
                        this.error = {};
                        this.$toasted.global.starface_success({
                            message: 'Änderungen gespeichert!',
                            icon: 'ACCOUNT',
                        });
                    } else {
                        const errorResponse =
                            typeof response.data === 'object' ?
                                response.data.error :
                                {};
                        this.error = errorResponse;
                        this.$toasted.global.starface_error({
                            message: 'Stammdaten konnten nicht aktualisiert werden',
                            icon: 'ACCOUNT',
                        });
                    }
                });
            },
            getUpdateData() {
                const putData = Object.assign({}, this.form);
                if (this.form.country in this.languageMapping) {
                    putData.country = this.languageMapping[this.form.country];
                }
                return putData;
            },
        },
    };
</script>

<template>
    <div class="container">
        <div id="account">
            <div class="account-header">
                <div class="account-header-title">
                    <NavigationHeader route="/" navigationText="zum Dashboard" title="Stammdaten"/>
                </div>
                <div class="account-header-actions">
                    <template v-if="previewMode">
                        <ButtonNormal @onClick="() => previewMode = !previewMode" class="button"
                                      buttonTitle="Bearbeiten" cssClass="btn btn-secondary"/>
                    </template>
                    <template v-else>
                        <ButtonNormal @onClick="this.submit" class="button" buttonTitle="Speichern"
                                      cssClass="btn btn-primary"/>
                        <ButtonNormal @onClick="() => {previewMode = !previewMode; this.init();}" class="button"
                                      buttonTitle="Abbrechen" cssClass="btn btn-secondary"/>
                    </template>
                </div>
            </div>
            <div class="account-content">
                <div class="form grid-column-1">
                    <div class="form-group">
                        <label for="customerId">Kundennummer:</label>
                        <input id="customerId" type="text" class="form-control" v-model="form.customer_id"
                               :readonly="true"/>
                        <AccountFormError :property="'customer_id'" :errorObj="error"/>
                    </div>
                    <div class="form-group">
                        <h1>Persönliche Daten</h1>
                    </div>
                    <div class="form-group">
                        <template v-if="previewMode">
                            <b>{{(form.salutation === 'mr' ? 'Herr' : 'Frau')}}</b>
                        </template>

                        <template v-else>
                            <input id="gender1" type="radio" v-model="form.salutation" value="mr"/>
                            <label for="gender1">HERR</label>
                            <input id="gender2" type="radio" v-model="form.salutation" value="ms"/>
                            <label for="gender2" :style="{marginLeft: '20px'}">FRAU</label>
                        </template>
                    </div>
                    <div class="form-group">
                        <label for="firstname">Vorname:</label>
                        <input id="firstname" type="text" class="form-control" v-model="form.firstname"
                               :readonly="previewMode"/>
                        <AccountFormError :property="'firstname'" :errorObj="error"/>
                    </div>
                    <div class="form-group">
                        <label for="lastname">Nachname:</label>
                        <input id="lastname" type="text" class="form-control" v-model="form.lastname"
                               :readonly="previewMode"/>
                        <AccountFormError :property="'lastname'" :errorObj="error"/>
                    </div>
                    <div class="form-group">
                        <label for="email">E-mail-Adresse:</label>
                        <input id="email" type="text" class="form-control" v-model="form.email"
                               :class="{invalid: !$v.form.email.email || !$v.form.email.required}"
                               :readonly="previewMode"/>
                        <span class="error-message" v-if="!$v.form.email.email">You have entered an invalid email address. Please try again!</span>
                        <span class="error-message" v-if="!$v.form.email.required">Email can't be empty! Please enter email address!</span>
                        <AccountFormError :property="'email'" :errorObj="error"/>
                    </div>
                    <div class="form-group"></div>
                    <div class="form-group">
                        <h1>Bankdaten</h1>
                    </div>
                    <div class="form-group">
                        <label for="bank">Bank:</label>
                        <input id="bank" type="text" class="form-control" v-model="form.bank" :readonly="previewMode"/>
                        <AccountFormError :property="'bank'" :errorObj="error"/>
                    </div>
                    <div class="form-group">
                        <label for="iban">IBAN:</label>
                        <input id="iban" type="text" class="form-control" v-model="form.iban" :readonly="previewMode"/>
                        <AccountFormError :property="'iban'" :errorObj="error"/>
                    </div>
                </div>
                <div class="form grid-column-2">
                    <div class="form-group">
                        <label for="company">Unternehmen:</label>
                        <input id="company" type="text" class="form-control" v-model="form.company" :readonly="true"/>
                        <AccountFormError :property="'company'" :errorObj="error"/>
                    </div>
                    <div class="form-group">
                        <h1>&nbsp;</h1>
                    </div>
                    <div class="form-group">
                        <label for="street">Straße:</label>
                        <input id="street" type="text" class="form-control" v-model="form.street"
                               :readonly="previewMode"/>
                        <AccountFormError :property="'street'" :errorObj="error"/>
                    </div>

                    <div class="form-group">
                        <label for="postalCode">PLZ:</label>
                        <input id="postalCode" type="text" class="form-control" v-model="form.zip"
                               :readonly="previewMode"/>
                        <AccountFormError :property="'zip'" :errorObj="error"/>
                    </div>

                    <div class="form-group">
                        <label for="city">Stadt:</label>
                        <input id="city" type="text" class="form-control" v-model="form.location"
                               :readonly="previewMode"/>
                        <AccountFormError :property="'location'" :errorObj="error"/>
                    </div>

                    <div class="form-group">
                        <label for="country">Land:</label>
                        <input id="country" type="text" class="form-control" v-model="form.country"
                               :readonly="previewMode"/>
                        <AccountFormError :property="'country'" :errorObj="error"/>
                    </div>
                    <div class="form-group">
                        <label for="phonenumber">Rufnummer:</label>
                        <input id="phonenumber" type="text" class="form-control" v-model="form.tel"
                               :readonly="previewMode"/>
                        <AccountFormError :property="'tel'" :errorObj="error"/>
                    </div>
                    <div class="form-group">
                        <h1>&nbsp;</h1>
                    </div>
                    <div class="form-group"></div>
                    <div class="form-group">
                        <label for="bic">BIC:</label>
                        <input id="bic" type="text" class="form-control" v-model="form.bic" :readonly="previewMode"/>
                        <AccountFormError :property="'bic'" :errorObj="error"/>
                    </div>
                </div>
            </div>
            <div class="account-header">
                <div class="account-header-actions" :style="{width: '100%'}" v-if="!previewMode">
                    <ButtonNormal @onClick="this.submit" class="button grid-column-1" buttonTitle="Speichern"
                                  cssClass="btn btn-primary"/>
                    <ButtonNormal @onClick="() => {previewMode = !previewMode; this.init();}" class="button grid-column-3"
                                  buttonTitle="Abbrechen" cssClass="btn btn-secondary"/>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '../../assets/scss/variables.scss';
    @import '../../assets/scss/inputs.scss';
    @import '../../assets/scss/form.scss';
    @import '../../assets/scss/grid.scss';

    #account {
        .account {
            &-content {
                display: grid;
                display: -ms-grid;
                grid-template-columns: 1fr 1fr;
                -ms-grid-columns: 1fr 1fr;
            }
            &-header {
                display: flex;
                justify-content: space-between;

                &-actions {
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: flex-end;
                    align-items: flex-end;
                }
                .button {
                    margin-bottom: 15px;
                    margin-left: 25px;
                    flex-basis: 240px;
                    flex-grow: 0;
                }
            }
            &-content {
                .form {
                    padding: 0;
                    .form-group {
                        font-size: 18px;
                        margin-bottom: 16px;
                        text-align: left;
                        min-height: 57px;
                        h1 {
                            font-weight: normal;
                            font-size: 28px;
                        }
                        .form-control {
                            width: 80%;
                            height: 24px;
                            line-height: 24px;
                            text-align: left;
                            &:focus {
                                outline: none;
                            }
                            &.valid {
                                border-color: green;
                            }
                            &.invalid {
                                border-color: $red;
                            }
                        }
                        input[type='radio']:checked + label {
                            color: $grey-dark;
                        }
                    }
                }
            }
        }
        input:read-only {
            border: none !important;
        }
    }
</style>
